import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Avatar,
  Divider,
  Tooltip,
  Alert,
  Collapse,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  People as PeopleIcon,
  AccountBalance as PayrollIcon,
  AccountBalanceWallet as AdvancesIcon,
  BeachAccess as LeaveIcon,
  Logout as LogoutIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  LocalAtm as LocalAtmIcon,
  Savings as MainFloatIcon,
  Inventory2 as InventoryIcon,
  EventAvailable as HolidayIcon,
  AccountTree as OrgChartIcon,
  Devices as AssetIcon,
  PlaylistAddCheck as ChecklistIcon,
  AccessTime as TimeIcon,
  DirectionsCar as DirectionsCarIcon,
  Grading as AppraisalIcon,
  School as TrainingIcon,
  Warning as WarningIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  HelpOutline as HelpOutlineIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { useCompany } from '../contexts/CompanyContext';
import { useSettings } from '../contexts/SettingsContext';
import { tokens } from '../theme/tokens';
import { formatEnumLabel } from '../utils/formatters';
import { isUnallocatedAllowedForCompanyCount } from '../utils/unallocatedAccess';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import CompanySelector from './CompanySelector';
import NotificationBell from './NotificationBell';
import { getReportBySlug } from './reports/reportConfig';

// ── Page-level components: route-level code splitting ────────────────────
// Every page below is loaded ON DEMAND via React.lazy + lazyWithRetry.
// This replaces the previous eager imports (see git history): the old
// attempt hung on infinite spinners when a stale index.html referenced old
// chunk hashes — lazyWithRetry now retries once and performs a rate-limited
// reload, so that failure mode cannot recur. Shell chrome + the Dashboard
// landing page stay eager for instant first paint.
// Shell chrome + landing page stay eager (small, needed for first paint)
import DashboardHome from './DashboardHome';
import GlobalSearch from './shared/GlobalSearch';
import BreadcrumbsBar from './shared/BreadcrumbsBar';
import DocumentTitle from './shared/DocumentTitle';
import NotificationPermission from './NotificationPermission';
import lazyWithRetry from '../utils/lazyWithRetry';

const AttendanceSalaryCalculator = lazyWithRetry(() => import('./AttendanceSalaryCalculator'), 'AttendanceSalaryCalculator');
const QuotationCalculator = lazyWithRetry(() => import('../QuotationCalculator'), 'QuotationCalculator');
const EmployeeList = lazyWithRetry(() => import('./EmployeeList'), 'EmployeeList');
const EmployeeFormPage = lazyWithRetry(() => import('./EmployeeFormPage'), 'EmployeeFormPage');
const EmployeeDetailsPage = lazyWithRetry(() => import('./EmployeeDetailsPage'), 'EmployeeDetailsPage');
const DocumentAlerts = lazyWithRetry(() => import('./DocumentAlerts'), 'DocumentAlerts');
const PayrollDashboard = lazyWithRetry(() => import('./PayrollDashboard'), 'PayrollDashboard');
const RunPayroll = lazyWithRetry(() => import('./RunPayroll'), 'RunPayroll');
const AdvancesManagement = lazyWithRetry(() => import('./AdvancesManagement'), 'AdvancesManagement');
const LeaveManagement = lazyWithRetry(() => import('./LeaveManagement'), 'LeaveManagement');
const CompanyManagement = lazyWithRetry(() => import('./CompanyManagement'), 'CompanyManagement');
const UserManagement = lazyWithRetry(() => import('./UserManagement'), 'UserManagement');
const MyProfile = lazyWithRetry(() => import('./MyProfile'), 'MyProfile');
const MyPayslip = lazyWithRetry(() => import('./MyPayslip'), 'MyPayslip');
const ExpenseClaimsManagement = lazyWithRetry(() => import('./ExpenseClaimsManagement'), 'ExpenseClaimsManagement');
const SubmitClaimWizard = lazyWithRetry(() => import('./SubmitClaimWizard'), 'SubmitClaimWizard');
const ExpenseClaimDetailsPage = lazyWithRetry(() => import('./ExpenseClaimDetailsPage'), 'ExpenseClaimDetailsPage');
const PettyCashManagement = lazyWithRetry(() => import('./PettyCashManagement'), 'PettyCashManagement');
const RecurringAllowancesManagement = lazyWithRetry(() => import('./RecurringAllowancesManagement'), 'RecurringAllowancesManagement');
const ReportsDashboard = lazyWithRetry(() => import('./ReportsDashboard'), 'ReportsDashboard');
const ReportPage = lazyWithRetry(() => import('./reports/ReportPage'), 'ReportPage');
const AuditLogViewer = lazyWithRetry(() => import('./AuditLogViewer'), 'AuditLogViewer');
const CompanyExpenses = lazyWithRetry(() => import('./CompanyExpenses'), 'CompanyExpenses');
const UnallocatedReceipts = lazyWithRetry(() => import('./UnallocatedReceipts'), 'UnallocatedReceipts');
const MainFloatManagement = lazyWithRetry(() => import('./MainFloatManagement'), 'MainFloatManagement');
const PublicHolidayCalendar = lazyWithRetry(() => import('./PublicHolidayCalendar'), 'PublicHolidayCalendar');
const SettingsPage = lazyWithRetry(() => import('./SettingsPage'), 'SettingsPage');
const OrgChart = lazyWithRetry(() => import('./OrgChart'), 'OrgChart');
const AssetManagement = lazyWithRetry(() => import('./AssetManagement'), 'AssetManagement');
const OnboardingOffboarding = lazyWithRetry(() => import('./OnboardingOffboarding'), 'OnboardingOffboarding');
const OvertimeManagement = lazyWithRetry(() => import('./OvertimeManagement'), 'OvertimeManagement');
const MRPPlanner = lazyWithRetry(() => import('./MRPPlanner'), 'MRPPlanner');
const PerformanceAppraisals = lazyWithRetry(() => import('./PerformanceAppraisals'), 'PerformanceAppraisals');
const TrainingRecords = lazyWithRetry(() => import('./TrainingRecords'), 'TrainingRecords');
const MyAttendance = lazyWithRetry(() => import('./MyAttendance'), 'MyAttendance');
const TeamAttendance = lazyWithRetry(() => import('./TeamAttendance'), 'TeamAttendance');
const CardPunchImport = lazyWithRetry(() => import('./CardPunchImport'), 'CardPunchImport');
const AttendanceFlagQueue = lazyWithRetry(() => import('./AttendanceFlagQueue'), 'AttendanceFlagQueue');
const DriverTrips = lazyWithRetry(() => import('./DriverTrips'), 'DriverTrips');
const NotificationsPage = lazyWithRetry(() => import('./NotificationsPage'), 'NotificationsPage');

const drawerWidth = 240;
const mobileDrawerWidth = 280;
const collapsedDrawerWidth = 72;

// ── Route access rules ────────────────────────────────────────────────
// Mirrors the sidebar menuSections roles arrays. Direct URL access to a
// disallowed route redirects to Dashboard with a "No access" toast and
// never renders the page. Role gate first, then module gates.
const ROUTE_ALL = ['employee', 'manager', 'finance', 'admin', 'super_admin'];
const ROUTE_MONEY = ['finance', 'admin', 'super_admin'];
const ROUTE_PEOPLE = ['manager', 'admin', 'super_admin'];
const ROUTE_LEAVE = ['employee', 'manager', 'admin', 'super_admin']; // finance excluded
const ROUTE_ADMIN = ['admin', 'super_admin'];

// Modules hidden when a manager/finance who manages multiple companies views a
// company other than their registered (home) company
// (user_profiles.default_company_id). Applies to BOTH the sidebar and
// RoleGuard (direct URL access redirects with the "No access" toast).
// Default behavior only — a per-user module override in
// user_profiles.permissions (Manage Users → Module Permissions) re-enables the
// module for that user even when viewing another company; so does a
// company_permissions override for the company being viewed.
const NON_HOME_HIDDEN_MODULES = {
  manager: ['mypayslip', 'advances', 'expense_claims', 'petty_cash', 'public_holidays', 'assets', 'training'],
  finance: ['mypayslip', 'advances', 'public_holidays'],
};

// Shared predicate for the sidebar (isItemVisible) and RoleGuard.
const isNonHomeHiddenModule = (userProfile, module, selectedCompanyId, companyCount) => {
  if (!module || companyCount <= 1) return false;
  const homeCompanyId = userProfile?.default_company_id;
  if (!homeCompanyId || selectedCompanyId === homeCompanyId) return false;
  if (!NON_HOME_HIDDEN_MODULES[userProfile?.role]?.includes(module)) return false;
  if (userProfile?.permissions?.[module] !== undefined) return false;
  if (userProfile?.company_permissions?.[selectedCompanyId]?.[module] !== undefined) return false;
  return true;
};

// Blocks the multi-company queue (Unallocated Receipts) for a user who
// manages only one company — unless they carry an explicit Unallocated
// Receipts override in Manage Users → Module Permissions (see
// src/utils/unallocatedAccess.js; the sidebar applies the same rule).
const SingleCompanyGuard = ({ children }) => {
  const { userProfile } = useAuth();
  const { companies, selectedCompanyId, loading } = useCompany();
  if (loading) return null;
  if (!isUnallocatedAllowedForCompanyCount(userProfile, companies.length, selectedCompanyId)) {
    return <Navigate to="/" replace state={{ noAccess: true }} />;
  }
  return children;
};

const RoleGuard = ({ roles, module, children }) => {
  const { userProfile, canAccessModule } = useAuth();
  const { isModuleVisible } = useSettings();
  const { selectedCompanyId, companies } = useCompany();
  // Profile still loading — ProtectedRoute already enforces auth; render nothing yet
  if (!userProfile) return null;
  const denied =
    (roles && !roles.includes(userProfile.role)) ||
    (module && (!isModuleVisible(module) || !canAccessModule(module))) ||
    isNonHomeHiddenModule(userProfile, module, selectedCompanyId, companies.length);
  if (denied) {
    return <Navigate to="/" replace state={{ noAccess: true }} />;
  }
  return children;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getDisplayName, userProfile, isAdmin, isManagerOrAbove, canAccessModule } = useAuth();
  const { selectedCompanyId, companies } = useCompany();
  const { isModuleVisible } = useSettings();
  const { checkNavigation } = useNavigationGuard();
  const [mobileOpen, setMobileOpen] = useState(false);
  // "No access" toast: shown when RoleGuard redirects a disallowed direct URL
  const [noAccessOpen, setNoAccessOpen] = useState(false);
  useEffect(() => {
    if (location.state?.noAccess) {
      setNoAccessOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // The header shows the FIRST NAME only — and employees.first_name can
  // itself hold two words (e.g. "Ali Husin"), so slicing display_name to its
  // first word is wrong. Fetch the real first name once for the linked
  // employee; fall back to the display-name heuristic when unavailable.
  const [headerFirstName, setHeaderFirstName] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const empId = userProfile?.employee_id;
    if (!empId || !isSupabaseConfigured()) {
      setHeaderFirstName(null);
      return undefined;
    }
    supabase
      .from('employees')
      .select('first_name')
      .eq('id', empId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setHeaderFirstName(data?.first_name || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userProfile?.employee_id]);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [expandedSections, setExpandedSections] = useState({
    Workspace: true,
    People: true,
    Payroll: true,
    Finance: true,
    Operations: true,
    Administration: true,
  });
  const [payrollMonth, setPayrollMonth] = useState(null);
  const [payrollYear, setPayrollYear] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [existingPayrollRecord, setExistingPayrollRecord] = useState(null);
  const [lastPayrollRecord, setLastPayrollRecord] = useState(null);
  const [processingForPayroll, setProcessingForPayroll] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Expiring documents alert state
  const [expiringDocs, setExpiringDocs] = useState([]);


  // Check for expiring documents on company change
  useEffect(() => {
    const checkExpiringDocuments = async () => {
      if (!selectedCompanyId || selectedCompanyId === 'all') {
        setExpiringDocs([]);
        return;
      }
      if (!isAdmin() && !isManagerOrAbove()) {
        setExpiringDocs([]);
        return;
      }

      const sessionKey = `expiry_check_${selectedCompanyId}`;
      const alreadyChecked = sessionStorage.getItem(sessionKey);

      try {
        const { getExpiringDocuments, getDocumentTypeLabel } = await import('../services/documentService');
        const { upsertNotification, getCompanyAdminsAndManagers } = await import('../services/notificationService');

        const result = await getExpiringDocuments(selectedCompanyId, 30);
        if (!result.success || result.data.length === 0) {
          setExpiringDocs([]);
          sessionStorage.setItem(sessionKey, 'checked');
          return;
        }

        const docs = result.data;
        setExpiringDocs(docs);

        // Only create bell notifications once per session
        if (!alreadyChecked) {
          const approvers = await getCompanyAdminsAndManagers(selectedCompanyId);
          if (approvers.length > 0) {
            const { supabase } = await import('../supabaseClient');
            const { data: approverProfiles } = await supabase
              .from('user_profiles')
              .select('id, employee_id')
              .in('id', approvers);

            // Each approver is only alerted about OTHER people's documents —
            // never their own (you can't act on your own expiry, and the
            // documents page isn't self-service for your own records).
            await Promise.all((approverProfiles || []).map(async (profile) => {
              const myDocs = profile.employee_id
                ? docs.filter((d) => d.employee_id !== profile.employee_id)
                : docs;
              if (myDocs.length === 0) return; // only their own docs were expiring — skip

              const nearestDoc = myDocs[0];
              const daysLeft = Math.ceil((new Date(nearestDoc.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));

              await upsertNotification({
                userId: profile.id,
                type: 'expiry_alert',
                title: 'Document Expiry Alert',
                message: `${myDocs.length} employee document${myDocs.length === 1 ? '' : 's'} expiring within 30 days. Nearest: ${nearestDoc.employees?.full_name || 'Employee'} - ${getDocumentTypeLabel(nearestDoc.document_type)} (${daysLeft} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} left).`,
                link: `/employees/${nearestDoc.employee_id}?tab=documents`,
                relatedTable: 'employee_documents',
                relatedRecordId: nearestDoc.id,
                companyId: selectedCompanyId,
              });
            }));
          }
          sessionStorage.setItem(sessionKey, 'checked');
        }
      } catch (err) {
        console.error('[Dashboard] Error checking expiring documents:', err);
        setExpiringDocs([]);
      }
    };

    checkExpiringDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebarCollapsed', String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const toggleSection = (title) => {
    setExpandedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const effectiveDrawerWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  // Auto-expand the sidebar section that contains the current route
  useEffect(() => {
    const activeSection = menuSections.find((section) =>
      section.items.some((item) => {
        if (location.pathname === item.path) return true;
        if (item.activePaths) {
          return item.activePaths.some((p) => location.pathname.startsWith(p));
        }
        return false;
      })
    );
    if (activeSection) {
      setExpandedSections((prev) => ({
        ...prev,
        [activeSection.title]: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      console.log('[Dashboard] Logout initiated...');
      const result = await logout();
      console.log('[Dashboard] Logout result:', result);
      // Navigate regardless of result to ensure user gets unstuck
      navigate('/login');
    } catch (error) {
      console.error('[Dashboard] Logout error:', error);
      // Force navigation even on error
      navigate('/login');
    }
  };

  const handleRunPayroll = (month, year) => {
    setPayrollMonth(month);
    setPayrollYear(year);
    // Navigate with month/year in URL params so they persist across refreshes
    navigate(`/payroll/run-payroll?month=${month}&year=${year}`);
  };

  const handleBackToPayrollDashboard = () => {
    setProcessingForPayroll(false);
    setSelectedEmployee(null);
    setExistingPayrollRecord(null);
    setPayrollMonth(null);
    setPayrollYear(null);
    // Increment refresh trigger to reload PayrollDashboard
    setRefreshTrigger(prev => prev + 1);
    navigate('/payroll');
  };

  const handleBackToRunPayroll = () => {
    // Navigate with month/year in URL params so they persist
    const targetUrl = (payrollMonth && payrollYear)
      ? `/payroll/run-payroll?month=${payrollMonth}&year=${payrollYear}`
      : '/payroll/run-payroll';

    console.log('handleBackToRunPayroll: Navigating to', targetUrl);
    navigate(targetUrl);

    // Delay state reset to ensure navigation completes first
    setTimeout(() => {
      console.log('handleBackToRunPayroll: Resetting state after navigation');
      setProcessingForPayroll(false);
      setSelectedEmployee(null);
      setExistingPayrollRecord(null);
      setLastPayrollRecord(null);
    }, 50);
  };

  const handleProcessEmployee = (employee, month, year, payrollRecord = null, lastRecord = null) => {
    setSelectedEmployee(employee);
    setExistingPayrollRecord(payrollRecord);
    setLastPayrollRecord(lastRecord);
    setPayrollMonth(month);
    setPayrollYear(year);
    setProcessingForPayroll(true);

    // Build URL with parameters for browser refresh support
    const params = new URLSearchParams({
      employee_id: employee.id,
      month: month,
      year: year
    });
    if (payrollRecord?.id) {
      params.append('payroll_record_id', payrollRecord.id);
    }
    navigate(`/payroll/calculator?${params.toString()}`);
  };

  const handleSavePayroll = () => {
    console.log('handleSavePayroll called, navigating to run-payroll with month:', payrollMonth, 'year:', payrollYear);
    // Trigger refresh of RunPayroll data
    setRefreshTrigger(prev => prev + 1);
    // Return to Run Payroll page after saving
    handleBackToRunPayroll();
  };

  const ALL_ROLES = ['employee', 'manager', 'finance', 'admin', 'super_admin'];
  const MONEY_ROLES = ['finance', 'admin', 'super_admin'];
  const PEOPLE_ROLES = ['manager', 'admin', 'super_admin'];
  const LEAVE_ROLES = ['employee', 'manager', 'admin', 'super_admin']; // finance excluded

  const menuSections = [
    {
      title: 'Workspace',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ALL_ROLES },
        { text: 'My Payslip', icon: <AssessmentIcon />, path: '/my-payslip', module: 'mypayslip', roles: ALL_ROLES },
        { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', module: 'reports', roles: MONEY_ROLES },
      ],
    },
    {
      title: 'People',
      items: [
        // Employee List: managers see their team only (enforced in EmployeeList)
        { text: 'Employee List', icon: <PeopleIcon />, path: '/employees', module: 'employees', roles: PEOPLE_ROLES, activePaths: ['/employees'] },
        { text: 'Org Chart', icon: <OrgChartIcon />, path: '/org-chart', module: 'org_chart', roles: ALL_ROLES },
        { text: 'Onboarding / Offboarding', icon: <ChecklistIcon />, path: '/onboarding', module: 'onboarding', roles: ['admin', 'super_admin'] },
        { text: 'Document Alerts', icon: <WarningIcon />, path: '/document-alerts', roles: ['admin', 'super_admin'] },
      ],
    },
    {
      title: 'Payroll',
      items: [
        { text: 'Payroll', icon: <PayrollIcon />, path: '/payroll', module: 'payroll', roles: MONEY_ROLES, activePaths: ['/payroll', '/payroll/run-payroll', '/payroll/calculator'] },
        { text: 'Advances', icon: <AdvancesIcon />, path: '/advances', module: 'advances', roles: ALL_ROLES },
        { text: 'Allowances & Bonuses', icon: <MoneyIcon />, path: '/allowances', roles: MONEY_ROLES },
      ],
    },
    {
      title: 'Finance',
      items: [
        // Claims & Petty Cash: all roles (employees/managers see own records only)
        { text: 'Expense Claims', icon: <ReceiptIcon />, path: '/expense-claims', module: 'expense_claims', roles: ALL_ROLES },
        { text: 'Petty Cash', icon: <LocalAtmIcon />, path: '/petty-cash', module: 'petty_cash', roles: ALL_ROLES },
        { text: 'Main Float', icon: <MainFloatIcon />, path: '/main-float', module: 'main_float', roles: MONEY_ROLES },
        { text: 'Company Expenses', icon: <BusinessIcon />, path: '/company-expenses', module: 'company_expenses', roles: MONEY_ROLES },
        { text: 'Unallocated Receipts', icon: <InventoryIcon />, path: '/unallocated-receipts', module: 'unallocated_receipts', roles: MONEY_ROLES },
      ],
    },
    {
      title: 'Operations',
      items: [
        // Leave & OT: employee = own, manager = own + team, admin = all; finance excluded
        { text: 'Leave Management', icon: <LeaveIcon />, path: '/leave', module: 'leave', roles: LEAVE_ROLES },
        { text: 'Overtime Requests', icon: <TimeIcon />, path: '/overtime', module: 'overtime', roles: LEAVE_ROLES },
        { text: 'My Attendance', icon: <TimeIcon />, path: '/my-attendance', module: 'attendance', roles: ['employee', 'manager', 'finance', 'admin'] },
        { text: 'Team Attendance', icon: <TimeIcon />, path: '/team-attendance', module: 'attendance', roles: ['manager', 'admin', 'super_admin'] },
        { text: 'Import Card Punches', icon: <UploadIcon />, path: '/attendance/import', module: 'attendance', roles: MONEY_ROLES },
        { text: 'Flagged Punches', icon: <TimeIcon />, path: '/attendance/flags', module: 'attendance', roles: ['admin', 'super_admin', 'finance', 'manager'] },
        { text: 'Driver Trips', icon: <DirectionsCarIcon />, path: '/driver-trips', module: 'attendance', roles: ['admin', 'super_admin', 'manager'] },
        { text: 'Public Holidays', icon: <HolidayIcon />, path: '/holidays', module: 'public_holidays', roles: ALL_ROLES },
        { text: 'Asset Management', icon: <AssetIcon />, path: '/assets', module: 'assets', roles: PEOPLE_ROLES },
        { text: 'Performance Appraisals', icon: <AppraisalIcon />, path: '/appraisals', module: 'appraisals', roles: LEAVE_ROLES },
        { text: 'Training Records', icon: <TrainingIcon />, path: '/training', module: 'training', roles: PEOPLE_ROLES },
        { text: 'MRP / BOM Planner', icon: <CalculateIcon />, path: '/mrp', roles: ['admin', 'super_admin'], module: 'mrp' },
      ],
    },
    {
      title: 'Administration',
      adminOnly: true,
      items: [
        { text: 'Manage Companies', icon: <BusinessIcon />, path: '/settings/companies', roles: ['super_admin'] },
        { text: 'Manage Users', icon: <PeopleIcon />, path: '/settings/users', roles: ['super_admin'] },
        { text: 'Audit Logs', icon: <AssessmentIcon />, path: '/audit-logs', roles: ['admin', 'super_admin'] },
        { text: 'System Settings', icon: <SettingsIcon />, path: '/settings', roles: ['super_admin'] },
        { text: 'Quotation Calculator', icon: <CalculateIcon />, path: '/quotation', roles: ['admin', 'super_admin'] },
      ],
    },
  ];

  const handleNavigation = (path) => {
    // Use navigation guard to check if we should block navigation
    const canNavigate = checkNavigation(() => {
      navigate(path);
    }, path);
    
    if (canNavigate) {
      navigate(path);
    }
    
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isItemVisible = (item) => {
    // Role gate: an item renders only if the user's role is explicitly allowed
    if (item.roles && !(userProfile && item.roles.includes(userProfile.role))) return false;
    // System-wide hidden modules disappear from the sidebar for everyone
    if (item.module && !isModuleVisible(item.module)) return false;
    // Unallocated Receipts is a multi-company queue: hidden for single-company
    // users unless they hold the explicit unallocated_receipts override
    // (SingleCompanyGuard applies the same rule on the route).
    if (item.path === '/unallocated-receipts' && !isUnallocatedAllowedForCompanyCount(userProfile, companies.length, selectedCompanyId)) {
      return false;
    }
    // Non-home company gate: manager/finance viewing a company other than their
    // registered one lose selected modules — unless they hold an explicit
    // per-user override for that module (user_profiles.permissions).
    if (isNonHomeHiddenModule(userProfile, item.module, selectedCompanyId, companies.length)) {
      return false;
    }
    // Module gate: role allowed AND module permission enabled
    if (item.module) return canAccessModule(item.module);
    return true;
  };

  const renderMenuItem = (item, isCollapsed = false) => {
    const selected =
      location.pathname === item.path ||
      (item.activePaths?.some((p) => location.pathname.startsWith(p)) ?? false);
    const button = (
      <ListItemButton
        selected={selected}
        onClick={() => handleNavigation(item.path)}
        sx={{
          borderRadius: isCollapsed ? '12px' : '0 20px 20px 0',
          mr: isCollapsed ? 0.5 : 1,
          ml: isCollapsed ? 0.5 : 0,
          mb: 0.5,
          minHeight: 48,
          px: isCollapsed ? 1 : 2,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          '&.Mui-selected': {
            bgcolor: tokens.color.primarySurface,
            color: tokens.color.primary,
            borderLeft: `3px solid ${tokens.color.primary}`,
            '& .MuiListItemIcon-root': { color: tokens.color.primary },
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 40, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
        {!isCollapsed && (
          <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: selected ? 600 : 500 }} />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
        {isCollapsed ? (
          <Tooltip title={item.text} placement="right" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const renderSection = (section, isCollapsed = false) => {
    const visibleItems = section.items.filter(isItemVisible);
    if (visibleItems.length === 0) return null;
    const isExpanded = expandedSections[section.title] !== false;

    return (
      <React.Fragment key={section.title}>
        {!isCollapsed && (
          <Box
            onClick={() => toggleSection(section.title)}
            sx={{
              px: 3,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                letterSpacing: '0.08em',
                fontSize: '11px',
                color: 'text.secondary', // muted rail divider, not a heading
              }}
            >
              {section.title}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
        )}
        <Collapse in={isCollapsed || isExpanded} timeout="auto" unmountOnExit={false}>
          {visibleItems.map((item) => renderMenuItem(item, isCollapsed))}
        </Collapse>
      </React.Fragment>
    );
  };

  const renderDrawer = (isCollapsed = false) => (
    <Box>
      <Toolbar sx={{ px: isCollapsed ? 1 : 2, justifyContent: isCollapsed ? 'center' : 'space-between' }}>
        {!isCollapsed && (
          <Typography variant="h6" color="primary" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
            BM SYSTEM
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && !isCollapsed && (
            <Tooltip title="Close menu" placement="right" arrow>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{ color: 'text.secondary', width: 44, height: 44 }}
                aria-label="Close menu"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isCollapsed ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
            <IconButton
              onClick={handleCollapseToggle}
              size="small"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                border: '2px solid',
                borderColor: 'primary.dark',
                boxShadow: 2,
                width: 34,
                height: 34,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      {isMobile && !isCollapsed && userProfile && (
        <Box sx={{ px: 2, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Avatar
              src={userProfile?.avatar_url || userProfile?.formal_photo_url || undefined}
              sx={{
                width: 40, height: 40,
                bgcolor: '#C7D2FE', color: '#3730A3', // tokens.avatarSurface / avatarText
                fontWeight: 600, fontSize: '12px',
              }}
            >
              {(!userProfile?.avatar_url && !userProfile?.formal_photo_url)
                && getDisplayName().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {getDisplayName()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatEnumLabel(userProfile.role)}
              </Typography>
            </Box>
          </Box>
          {(companies.length > 1 && (userProfile?.role === 'super_admin' || userProfile?.role === 'admin' || userProfile?.role === 'manager' || userProfile?.role === 'finance')) && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '12px' }}>
                Company
              </Typography>
              <CompanySelector variant="sidebar" />
            </Box>
          )}
        </Box>
      )}
      {!isMobile && !isCollapsed && userProfile && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          {(companies.length > 1 && (userProfile?.role === 'super_admin' || userProfile?.role === 'admin' || userProfile?.role === 'manager' || userProfile?.role === 'finance')) && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '12px' }}>
                Company
              </Typography>
              <CompanySelector variant="sidebar" />
            </Box>
          )}
        </Box>
      )}
      <List>
        {menuSections.map((section, index) => {
          if (section.adminOnly && !canAccessModule('settings')) return null;
          return (
            <React.Fragment key={section.title}>
              {index > 0 && <Divider sx={{ my: 1.5, mx: isCollapsed ? 1 : 2 }} />}
              {renderSection(section, isCollapsed)}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  const getPageTitle = () => {
    const allItems = menuSections.flatMap((s) => s.items);
    const exact = allItems.find((item) => item.path === location.pathname);
    if (exact) return exact.text;

    switch (location.pathname) {
      case '/dashboard':
        return 'Dashboard';
      case '/profile':
        return 'My Profile';
      case '/notifications':
        return 'Notifications';
      case '/payroll/run-payroll':
        return 'Run Payroll';
      case '/payroll/calculator':
        return 'Payroll Calculator';
      case '/settings/notifications':
        return 'Notification Preferences';
      default:
        return 'Business Management System';
    }
  };

  const getBreadcrumbItems = () => {
    // Nested attendance pages (checked before the exact menu-item match)
    if (location.pathname === '/attendance/import') {
      return [{ label: 'Attendance' }, { label: 'Import Card Punches' }];
    }

    const allItems = menuSections.flatMap((s) => s.items);
    const exact = allItems.find((item) => item.path === location.pathname);
    if (exact) return [{ label: exact.text }];

    // Report pages: /reports/<slug>
    if (location.pathname.startsWith('/reports/')) {
      const slug = location.pathname.split('/')[2];
      const report = getReportBySlug(slug);
      return [{ label: 'Reports', path: '/reports' }, { label: report ? report.name : 'Report' }];
    }

    // Nested expense-claims pages
    if (location.pathname === '/expense-claims/submit') {
      return [{ label: 'Expense Claims', path: '/expense-claims' }, { label: 'Submit Claim' }];
    }
    if (location.pathname.startsWith('/expense-claims/')) {
      return [{ label: 'Expense Claims', path: '/expense-claims' }, { label: 'Claim Details' }];
    }

    // Nested employee pages
    if (location.pathname === '/employees/new') {
      return [{ label: 'Employee List', path: '/employees' }, { label: 'Add Employee' }];
    }
    if (/^\/employees\/[^/]+\/edit$/.test(location.pathname)) {
      return [{ label: 'Employee List', path: '/employees' }, { label: 'Edit Employee' }];
    }
    if (/^\/employees\/[^/]+$/.test(location.pathname)) {
      return [{ label: 'Employee List', path: '/employees' }, { label: 'Employee Details' }];
    }

    // Special cases
    switch (location.pathname) {
      case '/dashboard':
        return [];
      case '/settings':
        return [{ label: 'Settings' }];
      case '/profile':
        return [{ label: 'My Profile' }];
      case '/notifications':
        return [{ label: 'Notifications' }];
      case '/payroll/run-payroll':
        return [{ label: 'Payroll', path: '/payroll' }, { label: 'Run Payroll' }];
      case '/payroll/calculator': {
        // Preserve the payroll period the calculator was opened for: Run
        // Payroll falls back to the CURRENT month when month/year params are
        // missing, which bounced users out of e.g. October into November.
        // Params live on the calculator URL (set by handleProcessEmployee);
        // Dashboard state is the fallback after an in-app state-only path.
        const calcParams = new URLSearchParams(location.search);
        const calcMonth = calcParams.get('month') || payrollMonth;
        const calcYear = calcParams.get('year') || payrollYear;
        return [
          { label: 'Payroll', path: '/payroll' },
          {
            label: 'Run Payroll',
            path: calcMonth && calcYear
              ? `/payroll/run-payroll?month=${calcMonth}&year=${calcYear}`
              : '/payroll/run-payroll',
          },
          { label: 'Calculator' },
        ];
      }
      case '/settings/notifications':
        return [{ label: 'Settings', path: '/settings' }, { label: 'Notification Preferences' }];
      default:
        return [];
    }
  };

  const renderContent = () => {
    return (
      <>
        <DocumentTitle pageName={getPageTitle()} />
        {/* Show warning if user profile doesn't exist - DISABLED FOR DEBUGGING */}
        {/*
        {!userProfile && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Complete Your Onboarding
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your login hasn't been linked to an employee record yet. Please contact your Super Admin to finalize your account setup.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" color="error" onClick={handleLogout} startIcon={<LogoutIcon />}>
                Logout
              </Button>
            </Box>
          </Alert>
        )}
        */}

        {/* Expiring Documents Alert Banner - only on dashboard and employee list pages */}
        {(() => {
          const isSnoozed = (id) => {
            const until = localStorage.getItem(`doc_alert_snooze_${id}`);
            if (!until) return false;
            if (Date.now() > parseInt(until, 10)) {
              localStorage.removeItem(`doc_alert_snooze_${id}`);
              return false;
            }
            return true;
          };
          const isDismissed = (id) => localStorage.getItem(`doc_alert_dismiss_${id}`) === '1';
          const visibleDocs = expiringDocs.filter((d) => !isSnoozed(d.id) && !isDismissed(d.id));
          return visibleDocs.length > 0 &&
            (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') &&
            (location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/employees' || location.pathname === '/document-alerts') && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate('/document-alerts')}
                >
                  View Documents
                </Button>
              }
            >
              <Typography variant="subtitle2" fontWeight="bold">
                {visibleDocs.length} Employee Document{visibleDocs.length !== 1 ? 's' : ''} Expiring Soon
              </Typography>
              <Typography variant="body2">
                Documents expiring within 30 days require renewal to stay compliant.
              </Typography>
            </Alert>
          );
        })()}

        <BreadcrumbsBar items={getBreadcrumbItems()} />

        <GlobalSearch />

        {/* Push notification permission prompt — only on dashboard */}
        {(location.pathname === '/' || location.pathname === '/dashboard') && (
          <NotificationPermission />
        )}

        <Snackbar
          open={noAccessOpen}
          autoHideDuration={4000}
          onClose={() => setNoAccessOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="warning" onClose={() => setNoAccessOpen(false)} variant="filled">
            No access — you don't have permission to view that page.
          </Alert>
        </Snackbar>

        {/* Route-level code split: the Suspense boundary below shows a
            spinner while a page chunk loads (lazyWithRetry handles stale
            chunk hashes with a retry + rate-limited reload). */}
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 320,
                py: 4,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          }
        >
        <Routes>
          <Route path="/" element={<DashboardHome />} />
          <Route path="/dashboard" element={<DashboardHome />} />

          <Route path="/payroll/calculator" element={
            <RoleGuard roles={ROUTE_MONEY} module="payroll">
              <AttendanceSalaryCalculator
                selectedEmployee={selectedEmployee}
                existingPayrollRecord={existingPayrollRecord}
                lastPayrollRecord={lastPayrollRecord}
                processingForPayroll={processingForPayroll}
                payrollMonth={payrollMonth}
                payrollYear={payrollYear}
                onSavePayroll={handleSavePayroll}
                onCancelPayroll={handleBackToRunPayroll}
              />
            </RoleGuard>
          } />

          <Route path="/quotation" element={<RoleGuard roles={ROUTE_ADMIN}><QuotationCalculator /></RoleGuard>} />
          <Route path="/mrp" element={<RoleGuard roles={ROUTE_ADMIN} module="mrp"><MRPPlanner /></RoleGuard>} />

          <Route path="/employees" element={<RoleGuard roles={ROUTE_PEOPLE} module="employees"><EmployeeList /></RoleGuard>} />
          <Route path="/employees/new" element={<RoleGuard roles={ROUTE_ADMIN} module="employees"><EmployeeFormPage /></RoleGuard>} />
          <Route path="/employees/:id/edit" element={<RoleGuard roles={ROUTE_ADMIN} module="employees"><EmployeeFormPage /></RoleGuard>} />
          <Route path="/employees/:id" element={<RoleGuard roles={ROUTE_PEOPLE} module="employees"><EmployeeDetailsPage /></RoleGuard>} />
          <Route path="/document-alerts" element={
            <RoleGuard roles={ROUTE_ADMIN}><DocumentAlerts /></RoleGuard>
          } />

          <Route path="/settings/companies" element={<RoleGuard roles={['super_admin']}><CompanyManagement /></RoleGuard>} />
          <Route path="/settings/users" element={<RoleGuard roles={['super_admin']}><UserManagement /></RoleGuard>} />
          <Route path="/profile" element={<MyProfile />} />

          <Route path="/payroll" element={
            <RoleGuard roles={ROUTE_MONEY} module="payroll">
              <PayrollDashboard onRunPayroll={handleRunPayroll} refreshTrigger={refreshTrigger} />
            </RoleGuard>
          } />

          <Route path="/payroll/run-payroll" element={
            <RoleGuard roles={ROUTE_MONEY} module="payroll">
              <RunPayroll
                onBack={handleBackToPayrollDashboard}
                onProcessEmployee={handleProcessEmployee}
                refreshTrigger={refreshTrigger}
              />
            </RoleGuard>
          } />

          <Route path="/advances" element={<RoleGuard roles={ROUTE_ALL} module="advances"><AdvancesManagement refreshTrigger={refreshTrigger} /></RoleGuard>} />

          <Route path="/leave" element={<RoleGuard roles={ROUTE_LEAVE} module="leave"><LeaveManagement /></RoleGuard>} />

          <Route path="/expense-claims/submit" element={<RoleGuard roles={ROUTE_ALL} module="expense_claims"><SubmitClaimWizard /></RoleGuard>} />
          <Route path="/expense-claims/:id" element={<RoleGuard roles={ROUTE_ALL} module="expense_claims"><ExpenseClaimDetailsPage /></RoleGuard>} />
          <Route path="/expense-claims" element={<RoleGuard roles={ROUTE_ALL} module="expense_claims"><ExpenseClaimsManagement /></RoleGuard>} />

          <Route path="/petty-cash" element={<RoleGuard roles={ROUTE_ALL} module="petty_cash"><PettyCashManagement /></RoleGuard>} />

          <Route path="/allowances" element={<RoleGuard roles={ROUTE_MONEY}><RecurringAllowancesManagement /></RoleGuard>} />

          <Route path="/company-expenses" element={<RoleGuard roles={ROUTE_MONEY} module="company_expenses"><CompanyExpenses /></RoleGuard>} />

          <Route path="/unallocated-receipts" element={<RoleGuard roles={ROUTE_MONEY} module="unallocated_receipts"><SingleCompanyGuard><UnallocatedReceipts /></SingleCompanyGuard></RoleGuard>} />
          <Route path="/main-float" element={<RoleGuard roles={ROUTE_MONEY} module="main_float"><MainFloatManagement /></RoleGuard>} />

          <Route path="/staff-floats" element={<Navigate to="/petty-cash?tab=advances" replace />} />

          <Route path="/holidays" element={<RoleGuard roles={ROUTE_ALL} module="public_holidays"><PublicHolidayCalendar /></RoleGuard>} />
          <Route path="/org-chart" element={<RoleGuard roles={ROUTE_ALL} module="org_chart"><OrgChart /></RoleGuard>} />
          <Route path="/assets" element={<RoleGuard roles={ROUTE_PEOPLE} module="assets"><AssetManagement /></RoleGuard>} />
          <Route path="/onboarding" element={<RoleGuard roles={ROUTE_ADMIN} module="onboarding"><OnboardingOffboarding /></RoleGuard>} />
          <Route path="/overtime" element={<RoleGuard roles={ROUTE_LEAVE} module="overtime"><OvertimeManagement /></RoleGuard>} />
          <Route path="/my-attendance" element={<RoleGuard roles={['employee', 'manager', 'finance', 'admin']} module="attendance"><MyAttendance /></RoleGuard>} />
          <Route path="/team-attendance" element={<RoleGuard roles={['manager', 'admin', 'super_admin']} module="attendance"><TeamAttendance /></RoleGuard>} />
          <Route path="/attendance/import" element={<RoleGuard roles={ROUTE_MONEY} module="attendance"><CardPunchImport /></RoleGuard>} />
          <Route path="/attendance/flags" element={<RoleGuard roles={ROUTE_ADMIN} module="attendance"><AttendanceFlagQueue /></RoleGuard>} />
          <Route path="/driver-trips" element={<RoleGuard roles={['admin', 'super_admin', 'manager']} module="attendance"><DriverTrips /></RoleGuard>} />
          <Route path="/appraisals" element={<RoleGuard roles={ROUTE_LEAVE} module="appraisals"><PerformanceAppraisals /></RoleGuard>} />
          <Route path="/training" element={<RoleGuard roles={ROUTE_PEOPLE} module="training"><TrainingRecords /></RoleGuard>} />

          <Route path="/my-payslip" element={<RoleGuard roles={ROUTE_ALL} module="mypayslip"><MyPayslip /></RoleGuard>} />

          <Route path="/reports" element={<RoleGuard roles={ROUTE_MONEY} module="reports"><ReportsDashboard /></RoleGuard>} />
          <Route path="/reports/:slug" element={<RoleGuard roles={ROUTE_MONEY} module="reports"><ReportPage /></RoleGuard>} />

          <Route path="/audit-logs" element={<RoleGuard roles={ROUTE_ADMIN}><AuditLogViewer /></RoleGuard>} />
          <Route path="/notifications" element={<NotificationsPage />} />

          <Route path="/settings" element={<RoleGuard roles={['super_admin']}><SettingsPage /></RoleGuard>} />
          <Route path="/settings/notifications" element={<RoleGuard roles={['super_admin']}><SettingsPage /></RoleGuard>} />
        </Routes>
        </Suspense>
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { md: `${effectiveDrawerWidth}px` },
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
          {/* Left: Hamburger + Product name */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1.5, display: { md: 'none' }, width: 44, height: 44 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                cursor: 'pointer',
                fontSize: { xs: '14px', sm: '16px' }, // in-scale: h3 → h2
                fontWeight: 600,
                lineHeight: { xs: '20px', sm: '24px' },
              }}
              onClick={() => navigate('/')}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                Business Management System
              </Box>
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                BM System
              </Box>
            </Typography>
          </Box>

          {/* Center: Global search (desktop only) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, justifyContent: 'center' }}>
            <TextField
              size="small"
              placeholder="Search..."
              onFocus={(e) => {
                window.dispatchEvent(new CustomEvent('open-global-search'));
                e.target.blur();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.6)' },
              }}
            />
          </Box>

          {/* Right: Notification + Avatar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: { xs: 0.5, sm: 1.5 }, flex: 1 }}>
            {userProfile && (
              <>
                <NotificationBell />
                <Box
                  onClick={(e) => setAvatarAnchorEl(e.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    borderRadius: 1,
                    px: 0.5,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                  }}
                  aria-label="Open user menu"
                  aria-haspopup="true"
                >
                  <Box
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: '#22c55e',
                        border: '2px solid',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <Avatar
                      src={userProfile?.avatar_url || undefined}
                      sx={{
                        width: 32, height: 32,
                        bgcolor: '#C7D2FE', color: '#3730A3', // tokens.avatarSurface / avatarText
                        fontWeight: 600, fontSize: '12px',
                      }}
                    >
                      {!userProfile?.avatar_url
                        && getDisplayName().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                    </Avatar>
                  </Box>
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {(() => {
                        // First name ONLY (employees.first_name — which may hold
                        // two words). Heuristic fallback only when the employee
                        // row is unavailable: email local part / first word.
                        if (headerFirstName) return headerFirstName;
                        const n = getDisplayName();
                        return n.includes('@') ? n.split('@')[0] : (n.trim().split(/\s+/)[0] || n);
                      })()}
                    </Typography>
                    {userProfile?.role && (
                      <Typography
                        variant="caption"
                        sx={{
                          lineHeight: 1.2,
                          color: 'rgba(255, 255, 255, 0.75)',
                          fontSize: '0.75rem',
                        }}
                      >
                        {formatEnumLabel(userProfile.role)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Menu
                  anchorEl={avatarAnchorEl}
                  open={Boolean(avatarAnchorEl)}
                  onClose={() => setAvatarAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      minWidth: 200,
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      mt: 1,
                    },
                  }}
                >
                  <Divider sx={{ borderColor: '#e2e8f0' }} />
                  <MenuItem
                    onClick={() => { setAvatarAnchorEl(null); navigate('/profile'); }}
                    sx={{ py: 1, px: 2, fontSize: '14px', '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <PersonIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} /> Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setAvatarAnchorEl(null); navigate('/settings'); }}
                    sx={{ py: 1, px: 2, fontSize: '14px', '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} /> Account Settings
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setAvatarAnchorEl(null); navigate('/settings/notifications'); }}
                    sx={{ py: 1, px: 2, fontSize: '14px', '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <NotificationsIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} /> Notification Preferences
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setAvatarAnchorEl(null); /* TODO: open help modal */ }}
                    sx={{ py: 1, px: 2, fontSize: '14px', '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ mr: 1.5, color: '#64748b' }} /> Help & Support
                  </MenuItem>
                  <Divider sx={{ borderColor: '#e2e8f0' }} />
                  <MenuItem
                    onClick={() => { setAvatarAnchorEl(null); handleLogout(); }}
                    sx={{ py: 1, px: 2, fontSize: '14px', color: '#ef4444', '&:hover': { bgcolor: '#f1f5f9' } }}
                  >
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} /> Log Out
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { md: effectiveDrawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          onOpen={handleDrawerToggle}
          PaperProps={{
            sx: {
              width: mobileDrawerWidth,
              boxSizing: 'border-box',
            },
          }}
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: { backgroundColor: 'rgba(0,0,0,0.5)' },
            },
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
          }}
        >
          {renderDrawer(false)}
        </SwipeableDrawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: effectiveDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {renderDrawer(collapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 }, // Smaller padding on mobile
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflow: 'auto', // Allow horizontal scroll if needed
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Container 
          maxWidth={false} // Allow full width on all screens
          sx={{
            px: { xs: 0.5, sm: 1, md: 2 }, // Responsive padding
          }}
        >
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
